import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnchoredPopover } from '../shared/AnchoredPopover';
import { siftApiClient } from '../../api/siftApiClient';
import type { BrokerSavedView } from '../../api/types';
import type { ActiveSavedViewRef, SavedViewScope } from './savedViewState';

/**
 * §14.10 — Saved auction / filter sets. Brokers have no Salesforce territory
 * auto-scoping, so every session they re-select the same market centers /
 * auctions / sellers + chip filters. A saved set snapshots the whole broker
 * filter selection under a name so it can be re-applied in one click.
 *
 * Two scopes, both server-side (durable + cross-device):
 *   - **My views** (personal) — private to the broker, stored in their
 *     `USERS.USER_CONFIG` blob.
 *   - **Shared views** (global) — visible to everyone on the Broker View, stored
 *     in the shared `VIEW_CONFIGS` row. Only brokers/admins may edit them.
 *
 * Legacy per-device `localStorage` sets are migrated into the personal store on
 * first load, then the local copy is cleared.
 */
export interface BrokerFilterSet {
  name: string;
  marketCenters: string[];
  auctions: string[];
  sellers: string[];
  vin: string;
  matchTypes: string[];
  priorities: string[];
  daysListed: number[];
  listingOcc: number[];
  totalBids: number[];
  reprice: number[];
  vdp: number[];
  workbook: number[];
  price: string[];
  conditions: string[];
  /** §8.4 Condition group direction (optional — absent in legacy saved sets; defaults to exclude). */
  conditionMode?: 'exclude' | 'include';
  engaged: string[];
  /** §14.5 VMA supply/demand level tokens (optional — absent in legacy saved sets). */
  supply?: string[];
  demand?: string[];
  /** Inspection provenance tokens to keep (optional — absent in legacy saved sets). */
  inspection?: string[];
  /** §10-D broker VIN-status tokens to keep (optional — absent in legacy saved sets). */
  status?: string[];
}

/** The current selection snapshot (everything but the name). */
export type BrokerFilterSelection = Omit<BrokerFilterSet, 'name'>;

const LEGACY_STORAGE_KEY = 'sift.brokerFilterSets';

/** Read any legacy per-device localStorage sets, for one-time migration. */
function takeLegacySets(): BrokerFilterSet[] {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as BrokerFilterSet[]) : [];
  } catch {
    return [];
  }
}

function clearLegacySets(): void {
  try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* non-fatal */ }
}

/** Count the non-empty groups in a selection (for the "N filters" summary). */
function selectionCount(s: BrokerFilterSelection): number {
  return (
    s.marketCenters.length + s.auctions.length + s.sellers.length + (s.vin.trim() ? 1 : 0) +
    s.matchTypes.length + s.priorities.length + s.daysListed.length + s.listingOcc.length +
    s.totalBids.length + s.reprice.length + s.vdp.length + s.workbook.length +
    s.price.length + s.conditions.length + s.engaged.length + (s.status?.length ?? 0) +
    (s.supply?.length ?? 0) + (s.demand?.length ?? 0) + (s.inspection?.length ?? 0)
  );
}

/** Coerce an opaque API saved view into a BrokerFilterSet (missing groups → []). */
function toFilterSet(v: BrokerSavedView): BrokerFilterSet {
  const arr = (k: string): string[] => (Array.isArray(v[k]) ? (v[k] as unknown[]).map(String) : []);
  const nums = (k: string): number[] => (Array.isArray(v[k]) ? (v[k] as unknown[]).map(Number) : []);
  return {
    name: String(v.name ?? ''),
    marketCenters: arr('marketCenters'),
    auctions: arr('auctions'),
    sellers: arr('sellers'),
    vin: typeof v.vin === 'string' ? v.vin : '',
    matchTypes: arr('matchTypes'),
    priorities: arr('priorities'),
    daysListed: nums('daysListed'),
    listingOcc: nums('listingOcc'),
    totalBids: nums('totalBids'),
    reprice: nums('reprice'),
    vdp: nums('vdp'),
    workbook: nums('workbook'),
    price: arr('price'),
    conditions: arr('conditions'),
    conditionMode: v.conditionMode === 'include' ? 'include' : 'exclude',
    engaged: arr('engaged'),
    supply: arr('supply'),
    demand: arr('demand'),
    inspection: arr('inspection'),
    status: arr('status'),
  };
}

/** Overwrite a same-named set (case-insensitive), then sort by name. */
function upsert(list: BrokerFilterSet[], next: BrokerFilterSet): BrokerFilterSet[] {
  const without = list.filter((s) => s.name.toLowerCase() !== next.name.toLowerCase());
  return [...without, next].sort((a, b) => a.name.localeCompare(b.name));
}

/** Re-label a set in place; a collision with another name absorbs that set. */
function rename(list: BrokerFilterSet[], from: string, to: string): BrokerFilterSet[] {
  const target = list.find((s) => s.name === from);
  if (!target) return list;
  return upsert(list.filter((s) => s.name !== from), { ...target, name: to });
}

/** The option lists the board currently knows about, used to flag stale saved values. */
export interface BrokerViewAvailable {
  marketCenters: string[];
  auctions: string[];
  sellers: string[];
}

interface StaleGroup { label: string; values: string[]; }

const STALE_GROUPS: { key: keyof BrokerViewAvailable; label: string }[] = [
  { key: 'marketCenters', label: 'Market Center' },
  { key: 'auctions', label: 'Auction' },
  { key: 'sellers', label: 'Seller' },
];

// Stale values are REPORTED, never dropped: an empty option list just means the
// board hasn't loaded, and a missing seller / auction can mean the broker's
// current scope rather than data that no longer exists.
function staleGroups(s: BrokerFilterSet, available?: BrokerViewAvailable): StaleGroup[] {
  if (!available) return [];
  return STALE_GROUPS.flatMap(({ key, label }) => {
    const options = available[key];
    if (!options || options.length === 0) return [];
    const missing = s[key].filter((v) => !options.includes(v));
    return missing.length > 0 ? [{ label, values: missing }] : [];
  });
}

function describeStale(groups: StaleGroup[]): string {
  return groups.map((g) => `${g.label}: ${g.values.join(', ')}`).join(' · ');
}

type Scope = SavedViewScope;

interface SavedFilterSetsProps {
  current: BrokerFilterSelection;
  onApply: (set: BrokerFilterSet, scope: Scope) => void;
  available?: BrokerViewAvailable;
  /** The saved view the board is currently showing, if any. */
  activeView?: ActiveSavedViewRef | null;
  /** The board's filters have been changed since the active view was applied. */
  activeModified?: boolean;
  /** Re-apply this view once the lists load (the view that was active before a refresh). */
  restoreView?: ActiveSavedViewRef | null;
  /** Called once after loading when ``restoreView`` is set. ``set`` is null when the
   *  view no longer exists; ``loaded`` is false when the lists could not be fetched. */
  onRestore?: (set: BrokerFilterSet | null, scope: Scope, loaded: boolean) => void;
  /** The active view was saved over, renamed, or deleted (null) from this menu. */
  onActiveViewChange?: (next: { set: BrokerFilterSet; scope: Scope } | null) => void;
}

export const SavedFilterSets: React.FC<SavedFilterSetsProps> = ({
  current, onApply, available, activeView, activeModified, restoreView, onRestore, onActiveViewChange,
}) => {
  const [personal, setPersonal] = useState<BrokerFilterSet[]>([]);
  const [globalViews, setGlobalViews] = useState<BrokerFilterSet[]>([]);
  const [canEditGlobal, setCanEditGlobal] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [renaming, setRenaming] = useState<{ scope: Scope; name: string } | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [staleNotice, setStaleNotice] = useState<{ name: string; groups: StaleGroup[] } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Mount-time values: the restore runs once, against the view active before reload.
  const restoreRef = useRef({ view: restoreView, cb: onRestore });

  // Load both scopes from the server; migrate any legacy localStorage sets once.
  useEffect(() => {
    let cancelled = false;
    const { view: toRestore, cb: restore } = restoreRef.current;
    (async () => {
      try {
        const resp = await siftApiClient.getBrokerSavedViews();
        if (cancelled) return;
        let mine = resp.personal.map(toFilterSet);
        const shared = resp.global.map(toFilterSet);
        setGlobalViews(shared);
        setCanEditGlobal(resp.can_edit_global);
        const legacy = takeLegacySets();
        if (mine.length === 0 && legacy.length > 0) {
          try {
            const saved = await siftApiClient.saveBrokerPersonalViews(legacy as unknown as BrokerSavedView[]);
            if (!cancelled) mine = saved.map(toFilterSet);
            clearLegacySets();
          } catch { /* keep legacy in localStorage if the migration write fails */ }
        }
        if (cancelled) return;
        setPersonal(mine);
        if (toRestore && restore) {
          const pool = toRestore.scope === 'global' ? shared : mine;
          const found = pool.find((v) => v.name === toRestore.name) ?? null;
          if (found) {
            setName(found.name);
            setShared(toRestore.scope === 'global');
          }
          restore(found, toRestore.scope, true);
        }
      } catch {
        /* offline / unauthorized — leave the menu empty, non-fatal */
        if (!cancelled && toRestore && restore) restore(null, toRestore.scope, false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) { setRenaming(null); return; }
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (wrapRef.current && t && wrapRef.current.contains(t)) return;
      // Portalled out of the wrapper, so its own contents would read as "outside".
      if (t?.closest?.('.bv-saved-views-menu')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const currentCount = useMemo(() => selectionCount(current), [current]);
  const trimmedName = name.trim();
  const total = personal.length + globalViews.length;

  // Apply a mutation to the freshest list, persist it write-through, and roll
  // back on failure. Using the functional updater avoids stale-closure races
  // when saves fire back-to-back.
  const mutatePersonal = useCallback((fn: (prev: BrokerFilterSet[]) => BrokerFilterSet[]) => {
    setPersonal((prev) => {
      const next = fn(prev);
      void (async () => {
        try {
          await siftApiClient.saveBrokerPersonalViews(next as unknown as BrokerSavedView[]);
        } catch {
          setPersonal(prev);
        }
      })();
      return next;
    });
  }, []);

  const mutateGlobal = useCallback((fn: (prev: BrokerFilterSet[]) => BrokerFilterSet[]) => {
    setGlobalViews((prev) => {
      const next = fn(prev);
      void (async () => {
        try {
          await siftApiClient.saveBrokerGlobalViews(next as unknown as BrokerSavedView[]);
        } catch {
          setGlobalViews(prev);
        }
      })();
      return next;
    });
  }, []);

  const isActive = useCallback(
    (scope: Scope, n: string) => !!activeView && activeView.scope === scope && activeView.name === n,
    [activeView],
  );

  // Saving the board's filters makes the board exactly that view, so it becomes
  // the active one (and is what a refresh brings back).
  const save = useCallback(() => {
    if (!trimmedName) return;
    const next: BrokerFilterSet = { name: trimmedName, ...current };
    const scope: Scope = shared && canEditGlobal ? 'global' : 'personal';
    if (scope === 'global') {
      mutateGlobal((prev) => upsert(prev, next));
    } else {
      mutatePersonal((prev) => upsert(prev, next));
    }
    onActiveViewChange?.({ set: next, scope });
    setName('');
  }, [trimmedName, current, shared, canEditGlobal, mutateGlobal, mutatePersonal, onActiveViewChange]);

  const removePersonal = useCallback((n: string) => {
    mutatePersonal((prev) => prev.filter((s) => s.name !== n));
    if (isActive('personal', n)) onActiveViewChange?.(null);
  }, [mutatePersonal, isActive, onActiveViewChange]);

  const removeGlobal = useCallback((n: string) => {
    mutateGlobal((prev) => prev.filter((s) => s.name !== n));
    if (isActive('global', n)) onActiveViewChange?.(null);
  }, [mutateGlobal, isActive, onActiveViewChange]);

  // Overwrite an existing set's body with the selection on the board right now,
  // keeping its name and scope. Without this, "editing" a set means retyping its
  // exact name to trigger the same-name overwrite.
  const updatePersonal = useCallback((n: string) => {
    mutatePersonal((prev) => upsert(prev, { name: n, ...current }));
    if (isActive('personal', n)) onActiveViewChange?.({ set: { name: n, ...current }, scope: 'personal' });
  }, [mutatePersonal, current, isActive, onActiveViewChange]);

  const updateGlobal = useCallback((n: string) => {
    mutateGlobal((prev) => upsert(prev, { name: n, ...current }));
    if (isActive('global', n)) onActiveViewChange?.({ set: { name: n, ...current }, scope: 'global' });
  }, [mutateGlobal, current, isActive, onActiveViewChange]);

  const commitRename = useCallback(() => {
    if (!renaming) return;
    const to = renameDraft.trim();
    if (to && to !== renaming.name) {
      const apply = (prev: BrokerFilterSet[]) => rename(prev, renaming.name, to);
      const list = renaming.scope === 'global' ? globalViews : personal;
      const target = list.find((v) => v.name === renaming.name);
      if (renaming.scope === 'global') mutateGlobal(apply);
      else mutatePersonal(apply);
      if (target && isActive(renaming.scope, renaming.name)) {
        onActiveViewChange?.({ set: { ...target, name: to }, scope: renaming.scope });
      }
    }
    setRenaming(null);
  }, [renaming, renameDraft, globalViews, personal, mutateGlobal, mutatePersonal, isActive, onActiveViewChange]);

  const iconBtnStyle: React.CSSProperties = {
    background: 'transparent', border: 'none', cursor: 'pointer', opacity: 0.6, padding: '4px 6px',
  };
  const rowStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0',
  };

  const renderRow = (s: BrokerFilterSet, scope: Scope, editable: boolean) => {
    const isRenaming = editable && renaming?.scope === scope && renaming.name === s.name;
    const onUpdate = scope === 'global' ? updateGlobal : updatePersonal;
    const onDelete = scope === 'global' ? removeGlobal : removePersonal;
    const stale = staleGroups(s, available);
    const active = isActive(scope, s.name);

    if (isRenaming) {
      return (
        <li key={s.name} style={rowStyle}>
          <input
            type="text"
            className="glass-input"
            style={{ flex: 1, minWidth: 0 }}
            value={renameDraft}
            maxLength={60}
            autoFocus
            aria-label={`New name for ${s.name}`}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') setRenaming(null);
            }}
          />
          <button
            type="button"
            className="bv-saved-view-rename-ok"
            onClick={commitRename}
            disabled={!renameDraft.trim()}
            aria-label={`Save new name for ${s.name}`}
            title="Save name"
            style={iconBtnStyle}
          >
            ✓
          </button>
          <button
            type="button"
            className="bv-saved-view-rename-cancel"
            onClick={() => setRenaming(null)}
            aria-label={`Cancel renaming ${s.name}`}
            title="Cancel"
            style={iconBtnStyle}
          >
            ✕
          </button>
        </li>
      );
    }

    return (
      <li
        key={s.name}
        style={active ? { ...rowStyle, background: '#eff6ff', borderRadius: 6 } : rowStyle}
        aria-current={active ? 'true' : undefined}
      >
        <button
          type="button"
          className={`bv-saved-view-apply${active ? ' bv-saved-view-apply--active' : ''}`}
          onClick={() => {
            onApply(s, scope);
            setStaleNotice(stale.length > 0 ? { name: s.name, groups: stale } : null);
            // Prefill the name box so re-saving obviously overwrites this set.
            setName(s.name);
            setShared(scope === 'global');
            setOpen(false);
          }}
          aria-label={`Apply "${s.name}"`}
          title={`Apply "${s.name}" (${selectionCount(s)} filters)`}
          style={{
            flex: 1, textAlign: 'left', background: 'transparent', border: 'none',
            cursor: 'pointer', padding: '6px 8px', borderRadius: 6, fontSize: '0.85rem',
            fontWeight: active ? 700 : undefined, color: active ? '#1d4ed8' : undefined,
          }}
        >
          {active && <span aria-hidden="true">✓ </span>}
          {s.name}
          {active && (
            <span style={{ opacity: 0.75, marginLeft: 6, fontSize: '0.7rem', fontWeight: 600 }}>
              {activeModified ? 'active · edited' : 'active'}
            </span>
          )}
          <span style={{ opacity: 0.6, marginLeft: 6, fontSize: '0.75rem' }}>· {selectionCount(s)}</span>
        </button>
        {stale.length > 0 && (
          <span
            title={`Values no longer on the board — ${describeStale(stale)}`}
            aria-label={`${s.name} has filter values that no longer exist`}
            style={{ flex: '0 0 auto', padding: '0 2px', color: '#b45309', cursor: 'help' }}
          >
            ⚠
          </span>
        )}
        {editable && (
          <>
            <button
              type="button"
              className="bv-saved-view-update"
              onClick={() => onUpdate(s.name)}
              aria-label={`Update saved view ${s.name} with the current filters`}
              title={`Overwrite "${s.name}" with the filters on the board now`}
              style={iconBtnStyle}
            >
              ↻
            </button>
            <button
              type="button"
              className="bv-saved-view-rename"
              onClick={() => { setRenaming({ scope, name: s.name }); setRenameDraft(s.name); }}
              aria-label={`Rename saved view ${s.name}`}
              title="Rename"
              style={iconBtnStyle}
            >
              ✎
            </button>
            <button
              type="button"
              className="bv-saved-view-del"
              onClick={() => onDelete(s.name)}
              aria-label={`Delete saved view ${s.name}`}
              title="Delete"
              style={iconBtnStyle}
            >
              ✕
            </button>
          </>
        )}
      </li>
    );
  };

  return (
    <div className="bv-saved-views" ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className={`bv-reset-btn${activeView ? ' bv-saved-views-btn--active' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title={activeView
          ? `The board is filtered by the saved view "${activeView.name}"${activeModified ? ', with changes since it was applied' : ''}. It stays applied when you refresh. Click to switch or save views; Reset clears it.`
          : 'Save the current filter selection or apply a saved one'}
      >
        {activeView ? (
          <>
            <span aria-hidden="true">★ </span>
            <span className="bv-saved-views-btn-name">{activeView.name}</span>
            {activeModified && <span className="bv-saved-views-btn-edited"> · edited</span>}
            {' ▾'}
          </>
        ) : (
          <>Saved views{total ? ` (${total})` : ''} ▾</>
        )}
      </button>
      {staleNotice && (
        <div
          role="status"
          style={{
            position: 'absolute', zIndex: 1200, top: 'calc(100% + 4px)', right: 0,
            display: 'flex', alignItems: 'flex-start', gap: 8, width: 320,
            padding: '8px 10px', fontSize: '0.72rem', lineHeight: 1.35,
            color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d',
            borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.14)',
          }}
        >
          <span>
            “{staleNotice.name}” uses{' '}
            {staleNotice.groups.reduce((n, g) => n + g.values.length, 0)} filter value
            {staleNotice.groups.reduce((n, g) => n + g.values.length, 0) === 1 ? '' : 's'}{' '}
            that are no longer on the board ({describeStale(staleNotice.groups)}). They are still
            applied, so results may be empty until you clear them.
          </span>
          <button
            type="button"
            onClick={() => setStaleNotice(null)}
            aria-label="Dismiss"
            title="Dismiss"
            style={{ flex: '0 0 auto', padding: '0 2px', color: '#92400e', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}
      {open && (
        <AnchoredPopover
          anchorRef={wrapRef}
          align="right"
          className="bv-saved-views-menu"
          role="listbox"
          style={{
            minWidth: 260,
            maxHeight: 'min(70vh, 520px)',
            overflowY: 'auto',
            background: 'var(--glass-bg, #fff)',
            border: '1px solid rgba(0,0,0,0.15)',
            borderRadius: 8,
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            padding: 8,
          }}
        >
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input
              type="text"
              className="glass-input"
              style={{ flex: 1, minWidth: 0 }}
              placeholder="Name this view…"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
            />
            <button type="button" className="bv-reset-btn" onClick={save} disabled={!trimmedName}>
              Save
            </button>
          </div>
          {canEditGlobal && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', opacity: 0.85, marginBottom: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
              Save as shared (visible to everyone)
            </label>
          )}
          <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: 6 }}>
            {currentCount === 0 ? 'No filters selected to save' : `Saves the ${currentCount} active filter${currentCount === 1 ? '' : 's'}`}
            {'. '}Use ↻ on a view to overwrite it with these.
          </div>

          {total === 0 ? (
            <div style={{ fontSize: '0.8rem', opacity: 0.6, padding: '6px 4px' }}>No saved views yet</div>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {personal.length > 0 && (
                <>
                  <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.55, padding: '4px 4px 2px' }}>My views</div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {personal.map((s) => renderRow(s, 'personal', true))}
                  </ul>
                </>
              )}
              {globalViews.length > 0 && (
                <>
                  <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.55, padding: '8px 4px 2px' }}>Shared views</div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {globalViews.map((s) => renderRow(s, 'global', canEditGlobal))}
                  </ul>
                </>
              )}
            </div>
          )}
        </AnchoredPopover>
      )}
    </div>
  );
};

